import React, { useState } from 'react';
import {
  FaRegStickyNote,
  FaList,
  FaLightbulb,
  FaCode,
  FaBookOpen,
  FaRandom,
  FaExpand,
  FaCompressArrowsAlt,
  FaRedo,
  FaRegCopy,
  FaBolt,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

/** Filled cap without tassel — reads clearly at small toggle sizes. */
const LearnModeIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M12 3L1 9L12 15L21 10.2V17H23V9L12 3Z" fill="currentColor" />
    <path d="M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="currentColor" />
  </svg>
);

/**
 * CardHeaderActions
 * Mid-solve surface: Check, Again, then notes. Everything else behind the chevron.
 */
const SECONDARY_PREF_KEY = 'flashcode_card_actions_secondary_v2';

const ModeToggle = ({ sessionMode, onChange, disabled, onHover, onLeave }) => {
  const modeClass =
    sessionMode === 'practice'
      ? 'card-header-actions__mode-toggle--practice'
      : sessionMode === 'learn'
        ? 'card-header-actions__mode-toggle--learn'
        : 'card-header-actions__mode-toggle--test';

  return (
    <div
      className={['card-header-actions__mode-toggle', modeClass].join(' ')}
      role="group"
      aria-label="Study mode"
    >
      <span className="card-header-actions__mode-toggle-slider" aria-hidden />
      <button
        type="button"
        className="card-header-actions__mode-toggle-btn"
        onClick={() => onChange('test')}
        disabled={disabled}
        onMouseEnter={() => onHover && onHover('Learn')}
        onMouseLeave={() => onLeave && onLeave()}
        aria-pressed={sessionMode === 'test'}
        title="Learn: type the full solution"
      >
        <LearnModeIcon className="card-header-actions__mode-toggle-icon" />
        <span className="card-header-actions__mode-toggle-label">Learn</span>
      </button>
      <button
        type="button"
        className="card-header-actions__mode-toggle-btn"
        onClick={() => onChange('learn')}
        disabled={disabled}
        onMouseEnter={() => onHover && onHover('Study')}
        onMouseLeave={() => onLeave && onLeave()}
        aria-pressed={sessionMode === 'learn'}
        title="Study: line-by-line with Byte coaching"
      >
        <FaBookOpen className="card-header-actions__mode-toggle-icon" aria-hidden />
        <span className="card-header-actions__mode-toggle-label">Study</span>
      </button>
      <button
        type="button"
        className="card-header-actions__mode-toggle-btn"
        onClick={() => onChange('practice')}
        disabled={disabled}
        onMouseEnter={() => onHover && onHover('Practice Mode')}
        onMouseLeave={() => onLeave && onLeave()}
        aria-pressed={sessionMode === 'practice'}
        title="Practice Mode: random or in-order review of started snippets"
      >
        <FaRandom className="card-header-actions__mode-toggle-icon" aria-hidden />
        <span className="card-header-actions__mode-toggle-label">Practice</span>
      </button>
    </div>
  );
};

const IconButton = ({
  Icon,
  active = false,
  onClick,
  onHover,
  onLeave,
  title,
  disabled,
  hoverLabel,
  className = ''
}) => (
  <button
    type="button"
    className={[
      'card-header-actions__icon-btn',
      active && 'card-header-actions__icon-btn--active',
      className
    ]
      .filter(Boolean)
      .join(' ')}
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    aria-pressed={active}
    onMouseEnter={() => onHover && onHover(hoverLabel || title)}
    onMouseLeave={() => onLeave && onLeave()}
  >
    <Icon aria-hidden />
  </button>
);

const CardHeaderActions = ({
  studyMode,
  sessionMode,
  onModeChange,
  onSessionModeChange,
  modeToggleDisabled = false,

  showNotes,
  hasNotes = false,
  onToggleNotes,

  variablesAvailable = false,
  variablesVisible = false,
  variablesCount = 0,
  onToggleVariables,

  showGlossary,
  onToggleGlossary,

  hintsAvailable = false,
  hintsVisible = false,
  hintsCount = 0,
  onToggleHints,

  isExpanded = false,
  onToggleExpand,

  onAgain,
  onTryAgain,
  againDisabled = false,
  againRevealed = false,

  onCopy,

  onSubmit,
  submitLabel = 'Check',
  submitDisabled = false,

  onHover,
  onLeave,

  availability = {}
}) => {
  const a = {
    notes: true,
    variables: true,
    glossary: true,
    hints: true,
    modeToggle: true,
    again: true,
    expand: true,
    copy: true,
    submit: true,
    ...availability
  };

  const [showSecondary, setShowSecondary] = useState(() => {
    try {
      return localStorage.getItem(SECONDARY_PREF_KEY) === 'true';
    } catch (_) {
      return false;
    }
  });

  const persistShowSecondary = (next) => {
    setShowSecondary(next);
    try {
      localStorage.setItem(SECONDARY_PREF_KEY, String(next));
    } catch (_) { /* storage unavailable */ }
  };

  const hasSecondaryItems =
    a.glossary ||
    a.copy ||
    (a.hints && hintsAvailable) ||
    a.modeToggle ||
    a.expand;

  return (
    <div className="card-header-actions" role="toolbar" aria-label="Card actions">
      <div className="card-header-actions__primary">
        {a.submit && (
          <button
            type="button"
            className="card-header-actions__submit-btn"
            onClick={onSubmit}
            disabled={submitDisabled}
            onMouseEnter={() => onHover && onHover(submitLabel)}
            onMouseLeave={() => onLeave && onLeave()}
            title={submitLabel}
          >
            <FaBolt
              className="card-header-actions__submit-btn-icon"
              aria-hidden
            />
            <span className="card-header-actions__submit-btn-text">
              {submitLabel}
            </span>
          </button>
        )}

        {a.again && !againRevealed && (
          <button
            type="button"
            className="card-header-actions__again-btn"
            onClick={onAgain}
            disabled={againDisabled}
            title="Again"
            aria-label="Again"
            onMouseEnter={() => onHover && onHover('Again')}
            onMouseLeave={() => onLeave && onLeave()}
          >
            <FaRedo className="card-header-actions__again-btn-icon" aria-hidden />
            <span className="card-header-actions__again-btn-text">Again</span>
          </button>
        )}

        {a.again && againRevealed && (
          <button
            type="button"
            className="card-header-actions__again-btn"
            onClick={onTryAgain}
            disabled={againDisabled}
            title="Try Again"
            aria-label="Try Again"
            onMouseEnter={() => onHover && onHover('Try Again')}
            onMouseLeave={() => onLeave && onLeave()}
          >
            <FaRedo className="card-header-actions__again-btn-icon" aria-hidden />
            <span className="card-header-actions__again-btn-text">Try Again</span>
          </button>
        )}

        {a.notes && (
          <button
            type="button"
            className={[
              'card-header-actions__notes-btn',
              hasNotes && !showNotes && 'card-header-actions__notes-btn--has-notes',
              showNotes && 'card-header-actions__notes-btn--active'
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onToggleNotes}
            title={
              showNotes
                ? 'Back to code'
                : hasNotes
                  ? 'Open study notes (saved)'
                  : 'Open study notes'
            }
            aria-label={
              showNotes
                ? 'Back to code'
                : hasNotes
                  ? 'Open study notes (saved)'
                  : 'Open study notes'
            }
            aria-pressed={showNotes}
            onMouseEnter={() =>
              onHover &&
              onHover(showNotes ? 'Back to code' : hasNotes ? 'Notes (saved)' : 'Notes')
            }
            onMouseLeave={() => onLeave && onLeave()}
          >
            <FaRegStickyNote className="card-header-actions__notes-btn-icon" aria-hidden />
          </button>
        )}

        {a.variables && variablesAvailable && (
          <button
            type="button"
            className={[
              'card-header-actions__variables-btn',
              variablesVisible && 'card-header-actions__variables-btn--active'
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onToggleVariables}
            title={
              variablesVisible
                ? 'Back to code'
                : `Show variable values (${variablesCount})`
            }
            aria-label={
              variablesVisible
                ? 'Back to code'
                : `Show variable values (${variablesCount})`
            }
            aria-pressed={variablesVisible}
            onMouseEnter={() =>
              onHover &&
              onHover(
                variablesVisible
                  ? 'Back to code'
                  : `Variables (${variablesCount})`
              )
            }
            onMouseLeave={() => onLeave && onLeave()}
          >
            <FaCode className="card-header-actions__variables-btn-icon" aria-hidden />
          </button>
        )}
      </div>

      {hasSecondaryItems && showSecondary && (
        <div
          className="card-header-actions__secondary"
          role="group"
          aria-label="More card actions"
        >
          {a.modeToggle && (
            <ModeToggle
              sessionMode={
                sessionMode ||
                (studyMode === 'lineByLine' ? 'learn' : 'test')
              }
              onChange={onSessionModeChange || onModeChange}
              disabled={modeToggleDisabled}
              onHover={onHover}
              onLeave={onLeave}
            />
          )}

          {a.glossary && (
            <IconButton
              Icon={FaList}
              active={showGlossary}
              onClick={onToggleGlossary}
              onHover={onHover}
              onLeave={onLeave}
              title={showGlossary ? 'Hide cookbook index' : 'Open cookbook index'}
              hoverLabel={showGlossary ? 'Close Index' : 'Index'}
              className="card-header-actions__icon-btn--cookbook-index"
            />
          )}

          {a.hints && hintsAvailable && (
            <IconButton
              Icon={FaLightbulb}
              active={hintsVisible}
              onClick={onToggleHints}
              onHover={onHover}
              onLeave={onLeave}
              title={hintsVisible ? 'Hide hints' : `Show hints (${hintsCount} available)`}
              hoverLabel={hintsVisible
                ? `Hide Hints (${hintsCount})`
                : `Show Hints (${hintsCount})`}
              className="card-header-actions__icon-btn--hint"
            />
          )}

          {a.copy && (
            <IconButton
              Icon={FaRegCopy}
              onClick={onCopy}
              onHover={onHover}
              onLeave={onLeave}
              title="Copy solution"
              hoverLabel="Copy"
            />
          )}

          {a.expand && (
            <IconButton
              Icon={isExpanded ? FaCompressArrowsAlt : FaExpand}
              onClick={onToggleExpand}
              onHover={onHover}
              onLeave={onLeave}
              title={isExpanded ? 'Collapse card' : 'Expand card'}
              hoverLabel={isExpanded ? 'Collapse' : 'Expand'}
            />
          )}
        </div>
      )}

      {hasSecondaryItems && (
        <IconButton
          Icon={showSecondary ? FaChevronLeft : FaChevronRight}
          onClick={() => persistShowSecondary(!showSecondary)}
          onHover={onHover}
          onLeave={onLeave}
          title={showSecondary ? 'Hide extra actions' : 'More actions'}
          hoverLabel={showSecondary ? 'Hide extras' : 'More actions'}
          className="card-header-actions__chevron-btn"
        />
      )}
    </div>
  );
};

export default CardHeaderActions;
